import React, {
    useEffect,
    useState,
} from 'react';
import { useNavigate } from "react-router-dom";

import {
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Card,
    IconButton,
    Typography,
    Tooltip,
    Switch,
    Collapse,
} from "@mui/material";

import {
    Delete,
    Edit,
    ExpandMore,
    Info,
    InfoOutlined,
    Lock
} from '@mui/icons-material';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TimeRangePickerList from './TimeRangePickerList';
import DateTimeRangePicker from './DateTimeRangePicker';
import RegulationPopover from './RegulationPopover';
import DateFormat from '../../utils/DateFormat';

import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../i18n';
import SubmittedPopover from './SubmittedPopover';
import DurationPickerDialog from './DurationPickerDialog';

import { workLoadForDay } from '../../model/contractInfo';
import { tr } from 'date-fns/locale';

/*
 ALL FUNCTIONS BELOW BELONG TO THE TIMECALCULATOR
 MODULE AND ARE SUBJECT OF A REFACTORING ISSUE
*/

// deep copy of array with entry objects
const copyEntryArray = (list) => {
    return list.map(e => {
        return {
            start: new Date(e.start),
            end: new Date(e.end),
        };
    });
};

// expects a sorted array if entries with start end
// removes || concats overlapping entries
const removeOverlaps = (list) => {
    let res = copyEntryArray(list)
    for (let i = 0; i < res.length - 1; i++) {
        if (res[i].end.getTime() >= res[i + 1].end.getTime()) {
            res.splice(i + 1, 1)
            i--
        } else if (res[i].end.getTime() >= res[i + 1].start.getTime()) {
            res[i].end.setTime(res[i + 1].end.getTime())
            res.splice(i + 1, 1)
            i--
        };
    };
    return res;
};

// filters an array of entries such that all entries fit into the bounds (start, end)
const filterWithBounds = (list, start, end) => {

    // filters all breaks that excluded by the new start and end time
    let filteredList = list.filter(be => {
        if (be.end.getTime() < start.getTime() || be.start.getTime() > end.getTime()) {
            return false;
        }
        return true;
    });

    // fixes all breakEntries that are "cut" by the new start and end time
    filteredList = filteredList.map(be => {
        let s = null;
        let e = null;

        if (be.start.getTime() < start.getTime()) {
            s = start;
        } else {
            s = be.start;
        };

        if (be.end.getTime() > end.getTime()) {
            e = end;
        } else {
            e = be.end;
        };

        return { start: s, end: e };
    });

    return filteredList
}

// finds the minimum and maximum time stamp of two arrays
const findBounds = (list1, list2) => {
    let t = [...list1, ...list2];

    let min = null;
    let max = null;

    for (let i = 0; i < t.length; i++) {
        if (!min) {
            min = t[i].start;
        };
        if (!max) {
            max = t[i].end;
        };

        if (t[i].start.getTime() < min.getTime()) {
            min = t[i].start;
        };
        if (t[i].end.getTime() > max.getTime()) {
            max = t[i].end;
        };
    };

    return { min, max }
}

// expects a sorted array of entries and
// creates an array of complementary entries
const fillGaps = (list) => {
    let res = []
    for (let i = 0; i < list.length - 1; i++) {
        if (list[i].end.getTime() < list[i + 1].start.getTime()) {
            res.push({ start: list[i].end, end: list[i + 1].start });
        };
    };
    return res;
};

const computeGaps = (list, start, end) => {
    let res = copyEntryArray(list);

    if (res.length === 0) {
        return [{ start: start, end: end }];
    };

    res.sort((a, b) => {
        return a.start - b.start;
    });

    res = [{ start: start, end: start }, ...res, { start: end, end: end }];
    res = removeOverlaps(res);
    res = fillGaps(res);

    return res;
};

const computeMiddleOfDay = (start, end) => {
    if (!(start && end)) {
        return null;
    };

    let diff = end.getTime() - start.getTime();
    let middle = end.getTime() - Math.trunc(diff / 2);

    let middleOfDay = new Date(middle);

    return middleOfDay;
};

const computeTimeDifference = (start, end) => {
    return end.getTime() - start.getTime();
};

const computeDurations = (entries) => {
    let res = copyEntryArray(entries)

    res.sort((a, b) => {
        return a.start - b.start;
    });

    res = removeOverlaps(res);

    let durations = res.map(e => computeTimeDifference(e.start, e.end));
    let sum = durations.reduce((acc, c) => acc + c, 0);

    return { durations, sum };
}

// returns true of array are different
const arraysAreDifferent = (arr1, arr2) => {
    if (arr1.length !== arr2.length) {
        return true;
    };

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i].start.getTime() !== arr2[i].start.getTime()) {
            return true;
        };
        if (arr1[i].end.getTime() !== arr2[i].end.getTime()) {
            return true;
        };
    };

    return false;
}



function TimeTrackingForm({ workDay, onSave, withLink, lastDayEnd, nextDayStart, isFlexTime, contractInfo }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [startDay, setStartDay] = useState(workDay.start);
    const [endDay, setEndDay] = useState(workDay.end);
    const [dayBoundError, setDayBoundError] = useState(false)
    const [workEntries, setWorkEntries] = useState(workDay.workEntries.length === 0 && startDay !== null && endDay !== null ? computeGaps(workDay.workEntries, startDay, endDay) : workDay.workEntries);
    const [workEntryErrors, setWorkEntryErrors] = useState(workEntries.map(w => false));
    const [breakEntries, setBreakEntries] = useState((startDay !== null && endDay !== null) ? computeGaps(workEntries, startDay, endDay) : []);
    const [breakEntryErrors, setBreakEntryErrors] = useState(breakEntries.map(b => false));
    const [endBusinessTrip, setEndBusinessTrip] = useState(null);
    const [businessTripError, setBusinessTripError] = useState(false)
    const [hasErrors, setHasErrors] = useState(false);

    const [middleOfDay, setMiddleOfDay] = useState(computeMiddleOfDay(startDay, endDay));

    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
    const [submitPopoverAnchorEl, setSubmitPopoverAnchorEl] = useState(null);
    const [compensation, setCompensation] = useState(workDay.compensation);
    const [contractWorkLoad, setContractWorkLoad] = useState(workDay.contractWorkLoad);
    const [insufficentBreakError, setInsufficentBreakError] = useState(false);
    const [regulationHour, setRegulationHour] = useState(6);
    const [regulationBreak, setRegulationBreak] = useState(30);
    const [coherentError, setCohrentError] = useState(false);
    const [gapError, setGapError] = useState(false);
    const [regulationError, setRegulationError] = useState(false);
    const [addedCredit, setAddedCredit] = useState(0);
    const [isChanged, setIsChanged] = useState(false);
    const [isAbsent, setIsAbsent] = useState(false);
    const [isBusinessTrip, setIsBusinessTrip] = useState(false);
    const [startBusinessTrip, setStartBusinessTrip] = useState(null);
    const [computedCompensation, setComputedCompensation] = useState(0);
    const [breaksExpanded, setBreaksExpanded] = useState(true);
    const [workExpanded, setWorkExpanded] = useState(false);
    const [isContractWorkLoadDialogOpen, setIsContractWorkLoadDialogOpen] = useState(false);
    const [isCompensationDialogOpen, setIsCompensationDialogOpen] = useState(false);
    const [updateWorkEntries, setUpdateWorkEntries] = useState(false);
    const [updateBreakEntries, setUpdateBreakEntries] = useState(false);
    const [creditedWorkTime, setCreditedWorkTime] = useState(0);
    const [creditedTravelTime, setCreditedTravelTime] = useState(0);

    const handleOpenPopover = (event) => {
        event.stopPropagation();
        setPopoverAnchorEl(event.currentTarget);
    };

    const handleOpenSubmittedPopover = (event) => {
        event.stopPropagation();
        setSubmitPopoverAnchorEl(event.currentTarget);
    };

    const handleCloseSubmittedPopover = (event) => {
        event.stopPropagation();
        setSubmitPopoverAnchorEl(null);
    };

    useEffect(() => {
        let hasChanges = false;

        if (workDay.start === null || startDay === null) {
            if (workDay.start === null ^ startDay === null) {
                hasChanges = true;
            };
        } else {
            if (startDay.getTime() !== workDay.start.getTime()) {
                hasChanges = true;
            };
        };

        if (workDay.end === null || endDay === null) {
            if (workDay.end === null ^ endDay === null) {
                hasChanges = true;
            };
        } else {
            if (endDay.getTime() !== workDay.end.getTime()) {
                hasChanges = true;
            };
        };

        if (workEntries.length !== workDay.workEntries.length) {
            hasChanges = true;
        } else {
            for (let i = 0; i < workEntries.length; i++) {
                let newEntry = workEntries[i];
                let oldEntry = workDay.workEntries[i];

                if (newEntry.start.getTime() !== oldEntry.start.getTime()) {
                    hasChanges = true;
                };

                if (newEntry.end.getTime() !== oldEntry.end.getTime()) {
                    hasChanges = true;
                };
            };
        };

        if (contractWorkLoad !== workDay.contractWorkLoad) {
            hasChanges = true;
        };

        if (compensation !== workDay.compensation) {
            hasChanges = true;
        };

        setIsChanged(hasChanges);
    }, [startDay, endDay, workEntries, workDay, contractWorkLoad, compensation]);

    useEffect(() => {
        if (!workDay.contractWorkLoad && workDay.workEntries.length === 0) {
            setIsAbsent(true);
        };
    }, [workDay]);

    useEffect(() => {
        if (isBusinessTrip) {
            const workDuration = computeDurations(workEntries).sum;
            let diff = workDay.contractWorkLoad - workDuration;

            if (diff < 0) {
                diff = 0;
            };

            setCreditedWorkTime(diff);

            if (startBusinessTrip && endBusinessTrip) {
                const tripDuration = endBusinessTrip.getTime() - startBusinessTrip.getTime();

                if (tripDuration < 0) {
                    setBusinessTripError(true);
                } else {
                    setBusinessTripError(false);
                    const relevantBreakEntries = computeGaps(workEntries.filter((we) => we.start >= startBusinessTrip && we.end <= endBusinessTrip), startBusinessTrip, endBusinessTrip);
                    const relevantWorkEntries = computeGaps(relevantBreakEntries, startBusinessTrip, endBusinessTrip);
                    let applicableTraveltime = tripDuration - computeDurations(relevantWorkEntries).sum;

                    applicableTraveltime -= diff;

                    if (applicableTraveltime < 0) {
                        applicableTraveltime = 0;
                    };

                    setCreditedTravelTime(Math.trunc(applicableTraveltime / 3));
                    setComputedCompensation(Math.trunc(applicableTraveltime / 3) + diff);
                    setCompensation(Math.trunc(applicableTraveltime / 3) + diff);
                };
            } else {
                setCreditedTravelTime(0);
                setComputedCompensation(diff);
                setCompensation(diff);
            };
        } else {
            setStartBusinessTrip(null);
            setEndBusinessTrip(null);
            setCreditedWorkTime(0);
            setCreditedTravelTime(0);
            setBusinessTripError(false);
        };
    }, [isBusinessTrip, startBusinessTrip, endBusinessTrip, workEntries, workDay]);

    const toggleAbsent = (absent) => {
        setIsAbsent(absent)
        if (absent) {
            setContractWorkLoad(0);
            handleResetWorkDay();
        } else {
            if (workDay.contractWorkLoad === 0) {
                if (contractInfo && workLoadForDay(contractInfo, workDay.date.getDay()) !== 0) {
                    setContractWorkLoad(workLoadForDay(contractInfo, workDay.date.getDay()));
                } else {
                    setContractWorkLoad(0);
                };

            } else {
                setContractWorkLoad(workDay.contractWorkLoad);
            };
            setCompensation(workDay.compensation);
        };
    };

    useEffect(() => {
        let val = computeDurations(workEntries).sum;

        if (compensation !== null) {
            val += compensation;
        };

        let diff = val - contractWorkLoad;
        setAddedCredit(diff);
    }, [workEntries, compensation, startDay, contractWorkLoad]);

    const handleClosePopover = (event) => {
        event.stopPropagation();
        setPopoverAnchorEl(null);
    };

    useEffect(() => {
        if (startDay !== null && endDay !== null) {
            regulationValidation(workEntries, breakEntries, startDay, endDay);
        };
    }, []);

    useEffect(() => {
        if (insufficentBreakError || coherentError || gapError) {
            setRegulationError(true);
        } else {
            setRegulationError(false);
        };
    }, [insufficentBreakError, coherentError, gapError]);

    useEffect(() => {
        if (!dayBoundError && startDay && endDay) {
            let middle = computeMiddleOfDay(startDay, endDay);
            setMiddleOfDay(middle);
        };
    }, [startDay, endDay]);

    useEffect(() => {
        let breakError = breakEntryErrors.some(b => b === true);
        let workError = workEntryErrors.some(w => w === true);
        let startEndNotEqualyPresent = startDay !== null ^ endDay !== null;

        if (breakError || workError || startEndNotEqualyPresent || dayBoundError) {
            setHasErrors(true);
        } else {
            setHasErrors(false);
        }
    }, [startDay, endDay, dayBoundError, breakEntryErrors, workEntryErrors]);

    const handleUpdateInfoAlert = (newWorkEntries, newBreakEntries) => {
        if (arraysAreDifferent(newWorkEntries, workEntries)) {
            setUpdateWorkEntries(true);
            setTimeout(() => setUpdateWorkEntries(false), 500);
        };

        if (arraysAreDifferent(newBreakEntries, breakEntries)) {
            setUpdateBreakEntries(true);
            setTimeout(() => setUpdateBreakEntries(false), 500);
        };
    };

    const handleChangeStartEnd = (start, end) => {
        // if one of both does not exist, then only set the dates and 
        // omit the rest of the function since computations rely on the presents 
        // of both, start and end

        if (!start || !end) {
            setStartDay(start);
            setEndDay(end);

            return;
        };

        // analog problem for invalid selection of start and end
        if (start.getTime() > end.getTime()) {
            setStartDay(start);
            setEndDay(end);
            setDayBoundError(true);

            return;
        };

        // break entries are used as the primary parameters, since in the case 
        // a user extends the times (eg : arrived sooner or leaves later), he will 
        // expect to have that time added to his work time

        // respects that case that a user might decrease the times (eg : arrived later or leaves earlier) 
        let newBreakEntries = filterWithBounds(breakEntries, start, end);

        // everything within the new bounds (start, end) that is not a break is now work time
        let newWorkEntries = computeGaps(newBreakEntries, start, end);

        handleUpdateInfoAlert(newWorkEntries, newBreakEntries);
        regulationValidation(newWorkEntries, newBreakEntries, start, end);
        setWorkEntries(newWorkEntries);
        setBreakEntries(newBreakEntries);
        setStartDay(start);
        setEndDay(end);
        setDayBoundError(false);
    };

    const handleResetWorkDay = () => {
        setStartDay(null);
        setEndDay(null);
        setWorkEntries([]);
        setWorkEntryErrors([false]);
        setBreakEntries([]);
        setBreakEntryErrors([false]);
        setMiddleOfDay(null);
        setDayBoundError(false);
        setInsufficentBreakError(false);
        setCohrentError(false);
        setGapError(false);
        setCompensation(0);
        setIsBusinessTrip(false);
    };

    const handleChangeBreakEntries = (entries) => {
        if (!(startDay && endDay)) {
            return;
        };

        let errors = validateStartEndErrors(entries);
        setBreakEntries(entries);
        setBreakEntryErrors(errors);

        if (!errors.some(e => e === true)) {
            let { min, max } = findBounds(entries, workEntries)
            let newWorkEntries = computeGaps(entries, min, max);
            handleUpdateInfoAlert(newWorkEntries, breakEntries);
            regulationValidation(newWorkEntries, entries, min, max);
            setWorkEntries(newWorkEntries);
            setStartDay(min);
            setEndDay(max);
        };
    };

    const handleChangeWorkEntries = (entries) => {
        if (!(startDay && endDay)) {
            return;
        };

        if (entries.length === 0) {
            if (startDay && endDay) {
                entries.push({ start: startDay, end: endDay });
            };
        };

        let errors = validateStartEndErrors(entries);
        setWorkEntries(entries);
        setWorkEntryErrors(errors);

        if (!errors.some(e => e === true)) {
            let { min, max } = findBounds(entries, breakEntries);
            let newBreakEntries = computeGaps(entries, min, max);
            handleUpdateInfoAlert(workEntries, newBreakEntries);
            regulationValidation(entries, newBreakEntries, min, max);
            setBreakEntries(newBreakEntries);
            setStartDay(min);
            setEndDay(max);
        };
    };

    const handleSave = () => {
        let newWorkEntries = [];
        let newBreakEntries = [];
        // this cleans up the workEntry array in case there where overlapping entries
        // only if startDay & endDay not null, otherwise newWorkEntries is an empty array anyways
        if (startDay && endDay) {
            newWorkEntries = computeGaps(breakEntries, startDay, endDay);
            newBreakEntries = computeGaps(newWorkEntries, startDay, endDay);

            setWorkEntries(newWorkEntries);
            setBreakEntries(newBreakEntries);
        };

        let newWorkDay = {
            ...workDay,
            start: startDay,
            end: endDay,
            workEntries: newWorkEntries,
            compensation: compensation,
            contractWorkLoad: contractWorkLoad,
        };

        onSave(newWorkDay);
    };

    const validateStartEndErrors = (list) => {
        let errors = list.map(e => {
            return e.start.getTime() > e.end.getTime();
        });
        return errors;
    };

    const regulationValidation = (newWorkEntries, newBreakEntries, min, max) => {
        const minute = 1000 * 60;
        const hour = minute * 60;

        let workDurations = computeDurations(newWorkEntries);
        let breakDurations = computeDurations(newBreakEntries);

        let regHour = workDurations.sum >= 9 * hour ? 9 : workDurations.sum > 6 * hour ? 6 : 0;
        let breakTime = regHour * 5 * minute;

        let validBreaks = breakDurations.durations.filter(d => d >= minute * 15);
        let sumValidBreaks = validBreaks.reduce((acc, c) => acc + c, 0);

        if (sumValidBreaks < breakTime) {
            setInsufficentBreakError(true);
        } else {
            setInsufficentBreakError(false);
        };

        if (workDurations.durations.filter(d => d > 6 * hour).length > 0) {
            setCohrentError(true);
        } else {
            setCohrentError(false);
        };

        let gapErr = false;

        if (lastDayEnd) {
            let diff = computeTimeDifference(lastDayEnd, min);
            if (diff < 11 * hour) {
                gapErr = true;
            };
        };

        if (nextDayStart) {
            let diff = computeTimeDifference(max, nextDayStart)
            if (diff < 11 * hour) {
                gapErr = true;
            };
        };

        setRegulationHour(regHour);
        setRegulationBreak(breakTime / minute);
        setGapError(gapErr);
    };

    return (
        <>
            <Box sx={{ minWidth: '600px' }}>

                {withLink ?
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={() => navigate(`/timekeeper/${workDay.id}`)} >
                            <ArrowForwardIosIcon fontSize='small' />
                            Details
                        </Button>
                    </Box>
                    :
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>
                            {workDay.date.toLocaleDateString(
                                i18n.language === 'de' ? 'de' : 'en-US',
                                { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }
                            )}
                            {workDay.submitted &&
                                <span>
                                    <IconButton sx={{ width: '20px', height: '20px', verticalAlign: 'top' }} onClick={handleOpenSubmittedPopover}>
                                        <Lock sx={{ width: '20px', height: '20px' }} />
                                    </IconButton>
                                </span>
                            }
                        </Typography>
                        <SubmittedPopover
                            open={Boolean(submitPopoverAnchorEl)}
                            anchorEl={submitPopoverAnchorEl}
                            onClose={handleCloseSubmittedPopover}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        />
                    </Box>
                }
                {isFlexTime &&
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Switch checked={isAbsent} onChange={(e) => toggleAbsent(e.target.checked)} />
                            <Typography>{t('toggle', { ns: 'timeKeepingPage', defaultValue: 'Arbeitsfrei (z.B. Wochenende, Krankheit, Urlaub)' })}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Switch checked={isBusinessTrip} onChange={(e) => setIsBusinessTrip(e.target.checked)} />
                            <Typography>{t('dienstreise', { ns: 'timeKeepingPage', defaultValue: 'Dienstreise' })}</Typography>
                        </Box>
                    </>
                }
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ p: 2, flex: '1' }}>
                        <DateTimeRangePicker
                            start={startDay}
                            end={endDay}
                            onChange={handleChangeStartEnd}
                            error={dayBoundError}
                            date={workDay.date}
                            labelStart={t('kommen', { ns: 'dashboardPage', defaultValue: 'Kommen' })}
                            labelEnd={t('gehen', { ns: 'dashboardPage', defaultValue: 'Gehen' })}
                            disabled={workDay.submitted}
                        />
                    </Box>
                    {isBusinessTrip &&
                        <Box sx={{ flex: '1' }}></Box>
                    }
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ minWidth: '600px' }}>

                        <Accordion
                            sx={{ border: 2, borderColor: regulationError ? 'darkOrange' : 'white', my: 1 }}
                            expanded={breaksExpanded}
                            onChange={() => { setBreaksExpanded(!breaksExpanded) }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: '40px' }}>
                                    <Typography sx={{ my: 1 }}>
                                        {t('pausen', { ns: 'dashboardPage', defaultValue: 'Pausen' })}
                                        {regulationError &&
                                            <IconButton onClick={handleOpenPopover} size='small' color='warning'>
                                                <Info />
                                            </IconButton>
                                        }
                                    </Typography>
                                    <Collapse in={updateBreakEntries}>
                                        <Alert icon={false} severity="success">
                                            Update
                                        </Alert>
                                    </Collapse>
                                    <Typography variant='subtitle1' sx={{ my: 1, textAlign: 'right' }}>{DateFormat.formatDuration(computeDurations(breakEntries).sum)}</Typography>
                                </Box>
                                <RegulationPopover
                                    open={Boolean(popoverAnchorEl)}
                                    anchorEl={popoverAnchorEl}
                                    onClose={handleClosePopover}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    insufficentBreakError={insufficentBreakError}
                                    coherentError={coherentError}
                                    gapError={gapError}
                                    regulationBreak={regulationBreak}
                                    regulationHour={regulationHour}
                                />
                            </AccordionSummary>
                            <AccordionDetails>
                                <TimeRangePickerList
                                    entryList={breakEntries}
                                    onUpdateEntryList={handleChangeBreakEntries}
                                    errors={breakEntryErrors}
                                    date={middleOfDay}
                                    disabled={workDay.submitted}
                                />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            sx={{ border: 2, borderColor: 'white', backgroundColor: 'white' }}
                            expanded={workExpanded}
                            onChange={() => { setWorkExpanded(!workExpanded) }}
                        >
                            <AccordionSummary
                                sx={{ border: 0, borderColor: 'white', backgroundColor: 'white' }}
                                expandIcon={<ExpandMore />}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: '40px' }}>
                                    <Typography variant='subtitle1' sx={{ my: 1 }}>{t('arbeitszeit', { ns: 'dashboardPage', defaultValue: 'Arbeitszeit' })}
                                        {isBusinessTrip && <span>
                                            <Tooltip title={'Reisezeit zählt grundsätzlich nicht als Arbeitszeit. Trage hier Start und Ende für das Dienstreisegeschäft Vorort bzw. für Arbeitszeiten unterwegs ein.'}>
                                                <InfoOutlined sx={{ verticalAlign: 'middle', ml: 1, color: 'gray' }} />
                                            </Tooltip>
                                        </span>}
                                    </Typography>
                                    <Collapse in={updateWorkEntries} >
                                        <Alert icon={false} severity="success">
                                            Update
                                        </Alert>
                                    </Collapse>
                                    <Typography variant='subtitle1' sx={{ my: 1, textAlign: 'right' }}>{DateFormat.formatDuration(computeDurations(workEntries).sum)}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TimeRangePickerList
                                    entryList={workEntries}
                                    onUpdateEntryList={handleChangeWorkEntries}
                                    errors={workEntryErrors}
                                    date={middleOfDay}
                                    disabled={workDay.submitted}
                                />
                            </AccordionDetails>
                        </Accordion>
                        {isFlexTime &&
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', border: 1, p: 2, borderColor: 'lightgray', my: 1, fontWeight: 'bold' }}>
                                    <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>{t('soll', { ns: 'timeKeepingPage', defaultValue: 'Soll' })}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>{DateFormat.formatDuration(contractWorkLoad)}</Typography>
                                        <IconButton size='small' sx={{ verticalAlign: 'top' }} onClick={() => setIsContractWorkLoadDialogOpen(true)}>
                                            <Edit fontSize='inherit' />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Box sx={{ border: 1, p: 2, borderColor: 'lightgray', my: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('geleistet', { ns: 'timeKeepingPage', defaultValue: 'Geleistete Stunden' })}</Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography>
                                                {DateFormat.formatDuration(computeDurations(workEntries).sum)}</Typography>
                                            <Box sx={{ width: ' 28px' }}></Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('individuell', { ns: 'timeKeepingPage', defaultValue: 'Individueller Anspruch auf Zeitausgleich' })}
                                            <span>
                                                <Tooltip title={<Trans ns='timeKeepingPage' i18nKey='claim'>Ein individueller Anspruch auf Zeitausgleich entsteht, wenn dir beispielsweise durch eine Dienstreise eine
                                                    Abweichung von deinen Soll-Stunden zusteht. Der Arbeitszeitrechner für Dienstreisen hilft dir bei der Berechnung dieses Anspruches.

                                                </Trans>}>
                                                    <InfoOutlined sx={{ verticalAlign: 'top', color: 'gray', ml: 1 }} />
                                                </Tooltip>
                                            </span>
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography sx={{ verticalAlign: 'bottom' }}>{DateFormat.formatDuration(compensation)}</Typography>
                                            <IconButton size='small' sx={{ verticalAlign: 'top' }} onClick={() => setIsCompensationDialogOpen(true)}>
                                                <Edit fontSize='inherit' />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <hr />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>{t('ist', { ns: 'timeKeepingPage', defaultValue: 'Ist' })}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>{DateFormat.formatDuration(computeDurations(workEntries).sum + compensation)}</Typography>
                                            <Box sx={{ width: ' 28px' }}></Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', border: 1, p: 2, borderColor: 'lightgray', my: 1 }}>
                                    <Typography sx={{ fontWeight: 'bold' }}>{t('arbeitszeitkonto', { ns: 'userSettingsPage', defaultValue: 'Arbeitszeitkonto' })}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: addedCredit < 0 ? 'red' : addedCredit === 0 ? '' : 'green' }}>{addedCredit > 0 ? '+' : ''}{DateFormat.formatDuration(addedCredit)}</Typography>
                                        <Box sx={{ width: ' 28px' }}></Box>
                                    </Box>
                                </Box>
                                <DurationPickerDialog
                                    id={'1'}
                                    value={contractWorkLoad}
                                    open={isContractWorkLoadDialogOpen}
                                    onClose={() => setIsContractWorkLoadDialogOpen(false)}
                                    handleSubmit={({ value }) => setContractWorkLoad(value)}
                                />
                                <DurationPickerDialog
                                    id={'2'}
                                    value={compensation}
                                    open={isCompensationDialogOpen}
                                    onClose={() => setIsCompensationDialogOpen(false)}
                                    handleSubmit={({ value }) => setCompensation(value)}
                                />
                            </>
                        }
                    </Box>
                    <Box>
                        {
                            isBusinessTrip &&
                            <Box>
                                <Card sx={{ minWidth: 600, p: 2 }}>
                                    <Typography sx={{ fontWeight: 'bold', mb: 3 }}>{t('arbeitszeitrechner', { ns: 'timeKeepingPage', defaultValue: 'Arbeitszeitrechner für Dienstreisen' })}</Typography>
                                        <Typography>{t('reisezeit', { ns: 'timeKeepingPage', defaultValue: 'Reisezeit' })}
                                            <span>
                                                <Tooltip title={t('reisezeitHinweis', { ns: 'timeKeepingPage', defaultValue: 'Gib bei eintägigen Dienstreisen Start und Ende des Arbeitstages inklusive Reisezeiten an. Bei mehrtägiger Reise gibst du Abfahrts- und Ankunftszeit am Wohnort/Dienstort/Unterkunft an.' })}>
                                                    <InfoOutlined sx={{ verticalAlign: 'top', ml: 1, color: 'gray' }} />
                                                </Tooltip>
                                            </span>
                                        </Typography>
                                    <DateTimeRangePicker
                                        start={startBusinessTrip}
                                        end={endBusinessTrip}
                                        labelStart={t('start', { ns: 'dashboardPage', defaultValue: 'Start' })}
                                        labelEnd={t('ende', { ns: 'dashboardPage', defaultValue: 'Ende' })}
                                        onChange={(start, end) => {
                                            setStartBusinessTrip(start);
                                            setEndBusinessTrip(end);
                                        }}
                                        onDelete={() => {
                                            setStartBusinessTrip(null);
                                            setEndBusinessTrip(null);
                                        }}
                                        error={businessTripError}
                                        date={workDay.date}
                                        disabled={false}
                                    />
                                    <Box sx={{ display: 'flex', mt: 3, justifyContent: 'space-between' }}>
                                        <Typography>{t('geleistet', { ns: 'timeKeepingPage', defaultValue: 'Geleistete Arbeitsstunden' })}</Typography>
                                        <Typography>
                                            {DateFormat.formatDuration(computeDurations(workEntries).sum)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', mt: 3, justifyContent: 'space-between' }}>
                                            <Typography>
                                                {t('anrechnungArbeit', { ns: 'timeKeepingPage', defaultValue: 'Angerechnete Arbeitsstunden' })}
                                            <span>
                                                <Tooltip title={t('vertragsGutschrift', { ns: 'timeKeepingPage', defaultValue: 'Auf Dienstreisen wird dir mindestens deine vertragliche Arbeitszeit angerechnet.' })}>
                                                    <InfoOutlined sx={{ verticalAlign: 'top', ml: 1, color: 'gray' }} />
                                                </Tooltip>
                                            </span>
                                        </Typography>
                                        <Typography>
                                            {creditedWorkTime > 0 ? '+' : creditedWorkTime < 0 ? '-' : ''}
                                            {DateFormat.formatDuration(creditedWorkTime)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('anrechnungReise', { ns: 'timeKeepingPage', defaultValue: 'Anrechnung Reisezeit' })}
                                            <span>
                                                <Tooltip title={
                                                    t('nichtAnrechenbar',
                                                        {
                                                            ns: "timeKeepingPage",
                                                            defaultValue: "Bei Dienstreisen, die länger als deine vertraglichen Sollstunden dauern, wird dir ein Drittel der nicht anrechenbaren Reisezeit auf dein Arbeitszeitkonto gutgeschrieben. Nicht anrechenbare Reisezeit ist die Fahrzeit, in der du nicht gearbeitet hast und die dir nicht bereits angerechnet wurde, um deine vertraglichen Sollstunden zu erreichen."
                                                        })
                                                }
                                                >
                                                    <InfoOutlined sx={{ verticalAlign: 'top', ml: 1, color: 'gray' }} />
                                                </Tooltip>
                                            </span>
                                        </Typography>
                                        <Typography>
                                            {creditedTravelTime > 0 ? '+' : creditedTravelTime < 0 ? '-' : ''}
                                            {DateFormat.formatDuration(creditedTravelTime)}
                                        </Typography>
                                    </Box>
                                    <hr />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('individuell', { ns: 'timeKeepingPage', defaultValue: 'Individueller Anspruch auf Zeitausgleich' })}</Typography>
                                        <Typography>{DateFormat.formatDuration(computedCompensation)}</Typography>
                                    </Box>
                                </Card>
                            </Box>
                        }
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, pb: 1 }}>
                    <IconButton onClick={handleResetWorkDay} disabled={workDay.submitted}>
                        <Delete />
                    </IconButton>
                    <Box sx={{ flex: '0.33' }}></Box>
                    <Box sx={{ flex: '0.33', textAlign: 'right' }}>
                        <Button variant='contained' onClick={handleSave} disabled={hasErrors || !isChanged || workDay.submitted}>
                            {isChanged && !workDay.submitted ? t('speichern', { ns: 'common', defaultValue: 'Speichern' }) : workDay.submitted ? t('freigegeben', { ns: 'dashboardPage', defaultValue: 'Freigegeben' }) : t('gespeichert', { ns: 'dashboardPage', defaultValue: 'Gespeichert' })}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default TimeTrackingForm
